import type { ContractCreationEligibility } from "@closer/shared";
import { CONTRACT_CREATION_BLOCK_NAMES } from "@closer/shared";
import { Button, Modal } from "@closer/ui";
import { useState } from "react";

import { PlusIcon } from "../components/icons";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/authContext";
import { NewContractCenter } from "./NewContractCenter";

/**
 * Üst Bardaki Yeni Sözleşme birincil aksiyonu (CLOS-101 `[062]`).
 *
 * ### İki katmanlı yetki — ve ikisinin işi farklı
 *
 * Düğme `can("contract.create")` ile gizleniyor; bu bir KOLAYLIK, güvenlik sınırı değil.
 * Karar tıklamada sunucuya soruluyor (`GET /api/contracts/creation-eligibility`). Kart
 * ikisini de şart koşuyor: *"yalnız ... permission'ı bulunan kullanıcı aksiyonu
 * kullanabilir"* ve *"permission kontrolü yalnız istemci tarafında düğme gizlemeye
 * dayanmaz."*
 *
 * İstemcinin elindeki yetki listesi bayat olabilir — rol tıklamadan bir saniye önce
 * değişmiş olabilir. Lisans durumu ise istemcide hiç yok.
 *
 * ### ⚠ Bu bileşen HİÇBİR ŞEY OLUŞTURMUYOR
 *
 * Kart: *"düğme yalnız oluşturma merkezini açar; gerçek kayıt seçilen yöntem ve
 * doğrulamalar tamamlanınca oluşturulur."* Buradan çıkan tek istek bir GET. Oluşturma
 * merkezinin içeriği (dosyadan, şablondan, boş sözleşme) CLOS-131'in işi; bu kart onun
 * ÖNKOŞULU.
 *
 * ### Engel neden toast DEĞİL
 *
 * Engel düğmenin yanında `role="alert"` ile kalıcı duruyor. Toast kaybolur; kartın
 * *"neden açıkça gösterilir"* kriteri, kullanıcının sebebi okuyup ne yapacağına karar
 * verebilmesini istiyor — üç saniyede silinen bir metin bunu karşılamaz. `role="alert"`
 * ekran okuyucuya da duyuruyor. Metin `@closer/shared`'dan geliyor; arayüz gerekçe
 * uydurmuyor (refleks 4).
 */
export function NewContractAction() {
  const { can, activeTenantId } = useAuth();
  const [open, setOpen] = useState(false);
  const [engeller, setEngeller] = useState<string[]>([]);
  const [sorgulaniyor, setSorgulaniyor] = useState(false);

  async function ac() {
    setSorgulaniyor(true);
    setEngeller([]);
    try {
      const { data } = await api<ContractCreationEligibility>(
        "/api/contracts/creation-eligibility",
        { tenantId: activeTenantId ?? undefined },
      );
      if (!data.allowed) {
        /**
         * Sebep KODLA geliyor, metin ortak sözleşmeden: sunucu ile arayüz aynı kelimeyi
         * kullanır ve iki yerde iki farklı açıklama doğmaz.
         *
         * ⚠ HEPSİ gösteriliyor (CLOS-973). Tek sebep gösterdiğimizde, iki sözleşme
         * kotası birden dolu olan kullanıcı önce arşivliyor, hiçbir şey değişmiyor ve
         * ikinci sebebi ancak o zaman öğreniyordu.
         */
        setEngeller(data.blocks.map((kod) => CONTRACT_CREATION_BLOCK_NAMES[kod]));
        return;
      }
      setOpen(true);
    } catch {
      /**
       * Ağ/sunucu arızası bir ENGEL DEĞİL: kullanıcının yetkisi ya da lisansı hakkında
       * hiçbir şey söylemiyor. Ayrı bir metinle ayrılıyor ki kullanıcı "yetkim yok"
       * sanıp yöneticisine boşuna gitmesin.
       */
      setEngeller(["Sözleşme oluşturma açılamadı"]);
    } finally {
      setSorgulaniyor(false);
    }
  }

  /**
   * Yetki kümesi bağlam yüklenene kadar boş; o aralıkta düğme çizilmiyor. Kap yine de
   * DOM'da duruyor ki yerleşim tıklama anında kaymasın ve testler "yüklendi ama düğme
   * yok" durumunu "henüz yüklenmedi"den ayırabilsin.
   */
  return (
    <div data-testid="yeni-sozlesme-kap" className="flex items-center gap-2">
      {can("contract.create") ? (
        /**
         * Üst barın TEK dolu düğmesi. UI Tur 2 §4: 48px, `plus` ikon + metin, radius-md —
         * `Button`'un kendi varsayılan yarıçapı zaten `radius-md` (train 2), bu yüzden
         * yalnız boyut `size="lg"` ile 48px'e çekiliyor; ayrı bir yuvarlaklık override'ı
         * KALDIRILDI (önceki sürüm tam yuvarlak bir hap kullanıyordu, bağlayıcı spec bunu
         * düzeltti).
         */
        <Button
          size="lg"
          icon={<PlusIcon />}
          aria-label="Yeni Sözleşme"
          className="max-sm:!w-12 max-sm:!px-0"
          onClick={() => void ac()}
          disabled={sorgulaniyor}
        >
          <span className="hidden sm:inline">Yeni Sözleşme</span>
        </Button>
      ) : null}

      {engeller.length > 0 ? (
        <div role="alert" className="flex max-w-64 flex-col gap-0.5">
          {engeller.map((metin) => (
            <p key={metin} data-testid="olusturma-engeli" className="text-xs text-danger">
              {metin}
            </p>
          ))}
        </div>
      ) : null}

      {/*
        Merkez CLOS-131 `[114]` ile geldi ve üç yöntemin üçü de artık gerçekten kayıt
        oluşturuyor: boş sözleşme `[069]`, dosyadan `[071]`, şablondan `[112]`. Üçü de
        oluşturulan sözleşmenin detayına yönlendiriyor (CLOS-626 ile düzeltildi — yorum
        "hiçbir şey oluşturmuyor" diyordu, kod aksini yapıyordu).
      */}
      <Modal open={open} onClose={() => setOpen(false)} title="Yeni Sözleşme">
        <NewContractCenter onClose={() => setOpen(false)} />
      </Modal>
    </div>
  );
}
